import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import { IItinerariesPage, IItinerariesSort, IItinerary, IItineraryDetail } from 'src/app/models';
import { UtilsService } from '../utils/utils.service';
import { ItinerariesHttpService } from './itineraries.http.service';

@Injectable({
  providedIn: 'root',
})
export class ItinerariesService {
  constructor(
    private itinerariesHttpService: ItinerariesHttpService,
    private utilsService: UtilsService,
  ) {}

  async getRouteItineraries(
    routeId: number,
    page: number,
    size: number,
    sort: IItinerariesSort,
  ): Promise<IItinerariesPage> {
    const source$ = this.itinerariesHttpService.getRouteItineraries(routeId, page, size, sort);
    const response = await lastValueFrom(source$);
    const { content, numberOfElements, totalElements, totalPages, first, last } = objectToCamel(
      response as object,
    ) as any;
    return {
      itineraries: (content as any[]).map((itinerary) => this.toItinerary(itinerary)),
      pagination: { numberOfElements, totalElements, totalPages, first, last, actualPage: page, size },
    };
  }

  async getItinerary(itineraryId: number): Promise<IItineraryDetail> {
    const source$ = this.itinerariesHttpService.getItinerary(itineraryId);
    const response = await lastValueFrom(source$);
    const { path, ...itinerary } = response as any;
    return { ...this.toItinerary(objectToCamel(itinerary) as object), path } as IItineraryDetail;
  }

  async getItineraries(itineraryIds: number[]): Promise<IItineraryDetail[]> {
    return Promise.all(itineraryIds.map((itineraryId) => this.getItinerary(itineraryId)));
  }

  async updateItineraryCode(itineraryId: number, code: string): Promise<IItinerary> {
    const source$ = this.itinerariesHttpService.updateItineraryCode(itineraryId, code);
    const response = await lastValueFrom(source$);
    return this.toItinerary(objectToCamel(response as object));
  }

  private toItinerary<T extends object>(itinerary: T): T & IItinerary {
    const { duration, createdAt, lastUsedAt } = itinerary as any;
    return {
      ...itinerary,
      formattedDuration: this.utilsService.minutesSecondsToDuration(duration),
      createdAt: createdAt ? this.utilsService.convertDate(createdAt) : null,
      lastUsedAt: lastUsedAt ? new Date(lastUsedAt) : null,
    } as T & IItinerary;
  }
}
